import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext.tsx';
import languageService from './services/languageService.ts';
import { ProtectedRoute } from './components/ProtectedRoute.tsx';
import { Layout } from './components/Layout.tsx';
import { Login } from './pages/Login.tsx';
import { Signup } from './pages/Signup.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import AcceptInvite from './pages/AcceptInvite.tsx';
import { AuthSuccess } from './pages/AuthSuccess.tsx';
import { EquipmentPage } from './pages/Equipment.tsx';
import Suppliers from './pages/Suppliers.tsx';
import Sites from './pages/Sites.tsx';
import Hires from './pages/Hires.tsx';
import Reminders from './pages/Reminders.tsx';
import { Users } from './pages/Users.tsx';
import { Profile } from './pages/Profile.tsx';
import Subscription from './pages/Subscription.tsx';
import { Integrations } from './pages/Integrations.tsx';
import { Zahara } from './pages/Zahara.tsx';
import SimpleMap from './pages/SimpleMap.tsx';
import HiresMapPage from './pages/HiresMapPage.tsx';
import Home from './pages/Home.tsx';

function App() {
  // Initialize language as early as possible
  useEffect(() => {
    languageService.initializeLanguage();
    // Debug after a short delay
    setTimeout(() => {
      languageService.debug();
    }, 1000);
  }, []);

  // Initialize dark mode from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    console.log('App.tsx: Initializing theme, savedDarkMode:', savedDarkMode);
    if (savedDarkMode) {
      const isDarkMode = JSON.parse(savedDarkMode);
      const htmlElement = document.documentElement;
      const theme = isDarkMode ? 'business' : 'corporate';
      console.log('App.tsx: Setting theme to:', theme, 'isDarkMode:', isDarkMode);
      htmlElement.setAttribute('data-theme', theme);
      if (isDarkMode) {
        htmlElement.classList.add('dark');
      } else {
        htmlElement.classList.remove('dark');
      }
      console.log('App.tsx: Final HTML attributes:', {
        'data-theme': htmlElement.getAttribute('data-theme'),
        'class': htmlElement.className
      });
    } else {
      console.log('App.tsx: No saved dark mode, setting default to corporate');
      const htmlElement = document.documentElement;
      htmlElement.setAttribute('data-theme', 'corporate');
      htmlElement.classList.remove('dark');
    }
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route path="/home" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/auth/success" element={<AuthSuccess />} />
            
            {/* Default route - Home page */}
            <Route path="/" element={<Home />} />
            
            {/* Protected routes */}
            <Route path="/app" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="equipment" element={<EquipmentPage />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="sites" element={<Sites />} />
              <Route path="hires" element={<Hires />} />
              <Route path="reminders" element={<Reminders />} />
              <Route path="users" element={<Users />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="zahara" element={<Zahara />} />
              <Route path="simple-map" element={<SimpleMap />} />
              <Route path="hires-map" element={<HiresMapPage />} />
              <Route path="profile" element={<Profile />} />
              <Route path="subscription" element={<Subscription />} />
            </Route>
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;